import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { useModuleData, ModuleLoadState } from "@/hooks/useModuleData";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAT_CHIP = { planned: "bg-slate-100 text-slate-500", ongoing: "bg-amber-50 text-amber-700 border border-amber-200", completed: "bg-blue-50 text-blue-700 border border-blue-200", published: "bg-emerald-50 text-emerald-700 border border-emerald-200", peer_reviewed: "bg-slate-900 text-white" };

const Stat = ({ label, value, sub }) => (
  <div className="p-5 border border-slate-200 bg-white">
    <p className="overline-label text-slate-400">{label}</p>
    <p className="font-heading font-extrabold text-3xl mt-2">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </div>
);

const Panel = ({ title, link, linkLabel, children, testid }) => (
  <section data-testid={testid} className="bg-white border border-slate-200">
    <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
      <p className="font-heading font-bold text-sm tracking-tight">{title}</p>
      {link && <Link to={link} className="text-xs font-semibold text-slate-500 hover:text-slate-900 flex items-center gap-1">{linkLabel} <ArrowRight size={12} /></Link>}
    </div>
    {children}
  </section>
);

const BarRow = ({ label, value, color = "#0F172A", right }) => (
  <div className="px-5 py-3">
    <div className="flex justify-between text-xs mb-1.5"><span className="font-semibold text-slate-800">{label}</span><span className="font-mono-data text-slate-500">{right ?? value}</span></div>
    <div className="h-1.5 bg-slate-100"><div className="h-full" style={{ width: `${value}%`, background: color }} /></div>
  </div>
);

export default function ExecutivePage() {
  const { productId } = useProduct();
  const m = useModuleData("executive", [
    { key: "pf", url: `${API}/portfolio-intelligence/${productId}`, label: "Portfolio intelligence" },
    { key: "sub", url: `${API}/submission-engine/${productId}`, label: "Submission engine" },
    { key: "dec", url: `${API}/decision-engine/${productId}`, label: "Decision engine" },
    { key: "ev", url: `${API}/evidence/${productId}`, label: "Evidence assets" },
    { key: "co", url: `${API}/countries`, label: "Market intelligence" },
    { key: "gap", url: `${API}/gap-engine/${productId}`, label: "Gap intelligence" },
  ], [productId]);
  const d = m.data && !m.error
    ? { pf: m.data.pf, sub: m.data.sub, dec: m.data.dec, ev: m.data.ev, co: m.data.co.countries, gap: m.data.gap }
    : null;

  return (
    <PageShell pageId="executive" overline="Presentation Layer · Persona Views" title="Executive Insights">
      {!d ? (
        <ModuleLoadState {...m} />
      ) : (
        <Tabs defaultValue="leadership">
          <TabsList className="bg-white border border-slate-200 rounded-none h-auto p-0 flex-wrap">
            {[["leadership", "Leadership"], ["director", "MA Director"], ["heor", "HEOR"], ["pricing", "Pricing & Access"], ["medical", "Medical Affairs"]].map(([v, l]) => (
              <TabsTrigger key={v} data-testid={`persona-${v}`} value={v} className="rounded-none px-5 py-3 text-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white">{l}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="leadership" className="mt-6 space-y-6">
            <div data-testid="leadership-kpis" className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="Portfolio Readiness" value={d.pf.portfolio_readiness.score} sub={`${d.pf.portfolio_readiness.distribution.Ready} markets ready now`} />
              <Stat label="Global Coverage" value={`${d.pf.global_evidence_coverage.weighted_coverage_index}%`} sub="weighted, all requirements" />
              <Stat label="Evidence Efficiency" value={d.pf.evidence_efficiency.score} sub={`avg reuse ${d.pf.evidence_efficiency.avg_global_reuse}/100`} />
              <Stat label="Top Opportunity" value={d.pf.market_access_opportunity.top_market} sub="access opportunity leader" />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <Panel title="Strategic Decisions — Headlines" link="/decisions" linkLabel="Full reasoning" testid="leadership-decisions">
                <div className="divide-y divide-slate-100">
                  {Object.values(d.dec.decisions).map((x) => (
                    <div key={x.question} className="px-5 py-3">
                      <p className="overline-label text-slate-400">{x.question}</p>
                      <p className="text-sm font-semibold text-slate-900 mt-1">{x.headline}</p>
                    </div>
                  ))}
                </div>
              </Panel>
              <Panel title="Expansion Sequence — Top 5" link="/roadmap" linkLabel="Full roadmap" testid="leadership-expansion">
                {d.pf.country_expansion_priority.sequence.slice(0, 5).map((e, i) => (
                  <BarRow key={e.country} label={`${i + 1}. ${e.flag} ${e.country} — ${e.tier}`} value={e.expansion_priority} />
                ))}
              </Panel>
            </div>
          </TabsContent>

          <TabsContent value="director" className="mt-6 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="Wave 1 — Submit Now" value={d.sub.summary.wave_1} />
              <Stat label="Wave 2 — Fast Follow" value={d.sub.summary.wave_2} />
              <Stat label="Parallel Clusters" value={d.sub.summary.parallel_clusters} />
              <Stat label="Avg Submission Readiness" value={d.sub.summary.avg_readiness} />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <Panel title="Launch Sequence & Readiness" link="/submissions" linkLabel="Submission plans" testid="director-sequence">
                {d.sub.submission_plans.map((p) => (
                  <BarRow key={p.country_id} label={`${p.flag} ${p.name} — Wave ${p.wave}`} value={p.submission_readiness_score}
                    color={p.wave === 1 ? "#047857" : p.wave === 2 ? "#B45309" : "#B91C1C"} right={`${p.submission_readiness_score} · ~${p.estimated_months_to_decision}mo`} />
                ))}
              </Panel>
              <Panel title="Submission Dependencies" link="/submissions" linkLabel="Details" testid="director-dependencies">
                <div className="divide-y divide-slate-100">
                  {d.sub.submission_dependencies.map((x, i) => (
                    <div key={i} className="px-5 py-3">
                      <p className="text-sm font-semibold text-slate-900">{x.before} → {x.after} <span className="text-[10px] uppercase tracking-widest text-slate-400 ml-1">{x.type.replace(/_/g, " ")}</span></p>
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed">{x.rationale}</p>
                    </div>
                  ))}
                </div>
              </Panel>
            </div>
          </TabsContent>

          <TabsContent value="heor" className="mt-6 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="Evidence Objects" value={d.ev.length} />
              <Stat label="Requirements Covered As-Is" value={d.gap.summary.covered_as_is} sub={`of ${d.gap.summary.total_requirements}`} />
              <Stat label="Reusable w/ Adaptation" value={d.gap.summary.reusable_with_adaptation} />
              <Stat label="Top Investment" value={d.pf.evidence_investment_priority.ranked_workstreams[0]?.workstream || "—"} />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <Panel title="Evidence Reuse Ranking" link="/reusability" linkLabel="Reuse matrix" testid="heor-reuse">
                {[...d.ev].sort((a, b) => b.intelligence.evidence_reuse_score - a.intelligence.evidence_reuse_score).map((a) => (
                  <div key={a.id} className="px-5 py-3">
                    <div className="flex justify-between items-center text-xs mb-1.5">
                      <span className="font-semibold text-slate-800 truncate mr-2">{a.name}</span>
                      <span className={`shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 ${MAT_CHIP[a.maturity]}`}>{a.maturity.replace("_", " ")}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100"><div className="h-full bg-emerald-700" style={{ width: `${a.intelligence.evidence_reuse_score}%` }} /></div>
                    <p className="font-mono-data text-[10px] text-slate-400 mt-1">reuse {a.intelligence.evidence_reuse_score} · accepted in {a.intelligence.countries_accepting.length} markets · {a.intelligence.localization_effort} localization effort</p>
                  </div>
                ))}
              </Panel>
              <Panel title="Evidence Generation Priorities" link="/gaps" linkLabel="Gap analysis" testid="heor-investments">
                {d.pf.evidence_investment_priority.ranked_workstreams.map((w) => (
                  <BarRow key={w.workstream} label={`${w.workstream}${w.country_specific ? " (local)" : ""}`} value={w.investment_priority} color="#B45309"
                    right={`${w.investment_priority} · ${w.markets_addressed.length} markets`} />
                ))}
              </Panel>
            </div>
          </TabsContent>

          <TabsContent value="pricing" className="mt-6 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="MEA Available" value={d.co.filter((c) => c.managed_entry_agreements?.available).length} sub={`of ${d.co.length} markets`} />
              <Stat label="High ERP Exposure" value={d.co.filter((c) => c.structural_profile.external_reference_pricing >= 70).length} sub="markets referencing prices" />
              <Stat label="Pricing Anchors" value={d.sub.submission_dependencies.filter((x) => x.type === "reference_pricing_anchor").length} sub="anchor dependencies mapped" />
              <Stat label="Translation Markets" value={d.co.filter((c) => c.dossier_language?.translation_required).length} sub="dossier translation required" />
            </div>
            <Panel title="Pricing Architecture by Market" testid="pricing-table">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-slate-200 text-left">
                    {["Market", "Pricing Mechanism", "ERP Exposure", "MEA", "Reassessment"].map((h) => <th key={h} className="px-5 py-3 overline-label text-slate-400 font-semibold">{h}</th>)}
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {d.co.map((c) => (
                      <tr key={c.id} data-testid={`pricing-row-${c.id}`}>
                        <td className="px-5 py-3 font-semibold whitespace-nowrap">{c.flag} {c.name}</td>
                        <td className="px-5 py-3 text-slate-600 max-w-[280px]">{c.pricing_mechanism}</td>
                        <td className="px-5 py-3">
                          <div className="w-20 h-1.5 bg-slate-100"><div className="h-full bg-blue-700" style={{ width: `${c.structural_profile.external_reference_pricing}%` }} /></div>
                        </td>
                        <td className="px-5 py-3">{c.managed_entry_agreements?.available ? <span className="text-emerald-700 font-semibold">{(c.managed_entry_agreements.types || [])[0] || "Available"}</span> : <span className="text-slate-400">None</span>}</td>
                        <td className="px-5 py-3 text-slate-600 max-w-[240px]">{c.reassessment_requirements}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </TabsContent>

          <TabsContent value="medical" className="mt-6 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="Ongoing Studies" value={d.ev.filter((a) => a.maturity === "ongoing").length} />
              <Stat label="Peer-Reviewed Assets" value={d.ev.filter((a) => a.maturity === "peer_reviewed").length} />
              <Stat label="Local Evidence Gaps" value={d.gap.country_specific_evidence.length} sub="irreducibly country-specific" />
              <Stat label="Advice Opportunities" value={d.sub.scientific_advice_opportunities.length} sub="recommended engagements" />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <Panel title="Country-Specific Evidence Needs" link="/gaps" linkLabel="Gap engine" testid="medical-local-needs">
                <div className="divide-y divide-slate-100">
                  {d.gap.country_specific_evidence.map((g, i) => (
                    <div key={i} className="px-5 py-3">
                      <p className="text-sm font-semibold text-slate-900">{g.flag} {g.country_name} — {g.requirement_type} <span className="text-[10px] uppercase tracking-widest text-slate-400 ml-1">{g.level}</span></p>
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed">{g.detail}</p>
                    </div>
                  ))}
                </div>
              </Panel>
              <Panel title="Scientific Advice Plan" link="/submissions" linkLabel="Submission plans" testid="medical-advice">
                <div className="divide-y divide-slate-100">
                  {d.sub.scientific_advice_opportunities.map((a, i) => (
                    <div key={i} className="px-5 py-3">
                      <p className="text-sm font-semibold text-slate-900">{a.flag} {a.country} <span className="text-[10px] uppercase tracking-widest text-amber-700 ml-1">{a.timing}</span></p>
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed">{a.provider} — {a.rationale}</p>
                    </div>
                  ))}
                </div>
              </Panel>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </PageShell>
  );
}
